'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { X, ExternalLink } from 'lucide-react';
import { PrivacyPolicyContent, CookiesPolicyContent, TermsContent } from './legal-content';

type LegalPage = 'privacy' | 'cookies' | 'terms';

interface LegalModalProps {
  page: LegalPage;
  open: boolean;
  onClose: () => void;
}

const PAGE_ROUTES: Record<LegalPage, string> = {
  privacy: '/privacy',
  cookies: '/cookies',
  terms: '/terms',
};

const PAGE_TITLE_KEYS: Record<LegalPage, string> = {
  privacy: 'privacy.title',
  cookies: 'cookies.title',
  terms: 'terms.title',
};

export function LegalModal({ page, open, onClose }: LegalModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('legal');
  const tModal = useTranslations('legalModal');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      contentRef.current?.scrollTo(0, 0);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const content = {
    privacy: <PrivacyPolicyContent />,
    cookies: <CookiesPolicyContent />,
    terms: <TermsContent />,
  }[page];

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#4e3529]/10 shrink-0">
          <h2 className="font-[family-name:var(--font-poppins)] text-lg font-semibold text-[#2c1810]">
            {t(PAGE_TITLE_KEYS[page])}
          </h2>
          <div className="flex items-center gap-2">
            <a
              href={PAGE_ROUTES[page]}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-[#4e3529]/50 hover:text-[#801515] transition-colors px-2 py-1.5 rounded-lg hover:bg-[#801515]/5"
              title={tModal('newTab')}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tModal('newTab')}</span>
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[#4e3529]/50 hover:text-[#801515] hover:bg-[#801515]/5 transition-colors"
              aria-label={tModal('close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div ref={contentRef} className="overflow-y-auto px-6 py-6 flex-1">
          {content}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[#4e3529]/10 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-[#801515] text-white rounded-xl text-sm font-medium hover:bg-[#601010] transition-colors"
          >
            {tModal('close')}
          </button>
        </div>
      </div>
    </div>
  );
}
